<script>
  import { createEventDispatcher } from 'svelte';
  import BasicInformation from './core/BasicInformation.svelte';
  import SelfNodeControls from './core/SelfNodeControls.svelte';
  import QuestionTypeSettings from './core/QuestionTypeSettings.svelte';
  import DatasetConfiguration from './core/DatasetConfiguration.svelte';
  import QuestionValidator from './core/QuestionValidator.svelte';
  import { questionsetStore } from '../../stores/questionsetStore';
  
  const dispatch = createEventDispatcher();
  
  export let question;
  export let questionIndex;
  export let isDraft = false;
  
  // Track if there are unsaved changes
  let hasUnsavedChanges = false;
  let originalQuestion = JSON.stringify(question);
  
  // Create working copy - use reactive statement to ensure it stays in sync
  let workingQuestion;
  $: workingQuestion = JSON.parse(JSON.stringify(question));
  
  // Validation state
  let validationExpanded = false;
  let showValidationPanel = true;
  
  // Update question field in working copy
  function updateWorkingQuestion(field, value) {
    // Create new reference to trigger reactivity
    workingQuestion = { 
      ...workingQuestion,
      [field]: value     
    };
    hasUnsavedChanges = JSON.stringify(workingQuestion) !== originalQuestion;
  }
  
  // Save changes to the main store
  function saveChanges() {
    try {
      if (isDraft) {
        questionsetStore.addQuestion(workingQuestion);
        dispatch('save', { question: workingQuestion });
      } else {
        questionsetStore.updateQuestion(questionIndex, workingQuestion);
        dispatch('save', { questionIndex, question: workingQuestion });
      }
      
      originalQuestion = JSON.stringify(workingQuestion);
      hasUnsavedChanges = false;
      
      if (isDraft) {
        dispatch('close');
      }
    } catch (error) {
      alert(`Error saving question: ${error.message}`);
    }
  }
  
  // Cancel changes
  function cancelChanges() {
    if (hasUnsavedChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        if (isDraft) {
          dispatch('cancelDraft', { questionIndex });
        }
        dispatch('close');
      }
    } else {
      if (isDraft) {
        dispatch('cancelDraft', { questionIndex });
      }
      dispatch('close');
    }
  }
  
  // Toggle validation panel
  function toggleValidationPanel() {
    showValidationPanel = !showValidationPanel;
  }
  
  // Watch for external question changes and reset working copy
  $: if (question && JSON.stringify(question) !== originalQuestion && !hasUnsavedChanges) {
    workingQuestion = JSON.parse(JSON.stringify(question));
    originalQuestion = JSON.stringify(question);
  }
</script>

<div class="edit-panel">
  <div class="panel-header" class:draft-header={isDraft}>
    <div class="header-info">
      <h3>
        {isDraft ? 'Draft Question:' : 'Edit Question:'} {workingQuestion?.id || 'New Question'}
        {#if hasUnsavedChanges}
          <span class="unsaved-indicator">*</span>
        {/if}
      </h3>
      {#if isDraft}
        <span class="draft-badge">DRAFT</span>
      {/if}
    </div>
    
    <div class="header-actions">
      <button 
        class="btn btn-small btn-secondary"
        on:click={toggleValidationPanel}
        class:active={showValidationPanel}
        title="Toggle validation panel"
      >
        🔍 Validate
      </button>
      
      {#if hasUnsavedChanges || isDraft}
        <button class="btn btn-primary" on:click={saveChanges}>
          {isDraft ? 'Save & Add' : 'Save Changes'}
        </button>
      {/if}
      
      <button class="btn close-btn" on:click={cancelChanges}>
        {isDraft ? 'Cancel' : '×'}
      </button>
    </div>
  </div>
  
  <div class="panel-content">
    
    <!-- Validation Panel -->
    {#if showValidationPanel}
      <QuestionValidator 
        question={workingQuestion}
        {updateWorkingQuestion}
        bind:expanded={validationExpanded}
        showAutoFix={true}
      />
    {/if}
    
    <!-- Basic Information -->
    <BasicInformation 
      question={workingQuestion}
      {updateWorkingQuestion} 
    />
    
    <!-- Self Node Controls -->
    <SelfNodeControls 
      question={workingQuestion}
      {updateWorkingQuestion}
      bind:hasUnsavedChanges
    />
    
    <!-- Question Type Settings -->
    <QuestionTypeSettings 
      question={workingQuestion}
      {updateWorkingQuestion} 
      bind:hasUnsavedChanges
    />
    
    <!-- Dataset Configuration -->
    <DatasetConfiguration 
      question={workingQuestion}
      {updateWorkingQuestion}
      bind:hasUnsavedChanges
      {questionIndex}
    />
    
  </div>
</div>

<style lang="scss">
  .edit-panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 500px;
    background: #f6f6f6;
    border-left: 4px solid #d4d3bc;
    z-index: 40;
    display: flex;
    flex-direction: column;
    
    @media (max-width: 1200px) {
      width: 450px;
    }
    
    @media (max-width: 900px) {
      width: 100%;
      left: 0;
    }
  }
  
  .panel-header {
    padding: 20px;
    border-bottom: 4px solid #d4d3bc;
    background: #f3f2e3;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    
    &.draft-header {
      background: #fef3c7;
      border-bottom-color: #f59e0b;
    }
  }
  
  .header-info {
    display: flex;
    flex-direction: column;
    gap: 5px;
    
    h3 {
      margin: 0;
      font-size: 1rem;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
  
  .header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  
  .unsaved-indicator {
    color: #f59e0b;
    font-weight: bold;
  }
  
  .draft-badge {
    background: #f59e0b;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 0.5px;
  }
  
  .close-btn {
    background: #fc72cd;
    color: white;
    
    &:hover {
      background: #fc72cdd9;
    }
  }
  
  .panel-content {
    flex: 1;
    overflow: auto;
    padding: 20px;
  }
  
  .btn {
    padding: 6px 12px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    
    &.btn-primary {
      background: #3b82f6;
      color: white;
      
      &:hover {
        background: #2563eb;
      }
    }
    
    &.btn-secondary {
      background: #6b7280;
      color: white;
      
      &:hover {
        background: #4b5563;
      }
      
      &.active {
        background: #10b981;
        
        &:hover {
          background: #059669;
        }
      }
    }
    
    &.btn-small {
      padding: 4px 8px;
      font-size: 11px;
    }
  }
</style>